/**
 * The game socket.
 *
 * Decodes what the server sends into events, and encodes local intent going out.
 * It holds no game state of its own: everything decoded is handed straight to the
 * world, so there is exactly one copy of the truth on this side too.
 */

import {
  ByteReader,
  decodeAdminFacts,
  decodeHoldCamera,
  decodeTriggers,
  decodeAdminState,
  decodeAppearance,
  decodeDied,
  decodeHit,
  decodeBonuses,
  decodeChatMessage,
  decodeContainerUpdate,
  decodeAnimation,
  decodeCraftingState,
  decodeDropTable,
  decodeGathering,
  decodeDialogue,
  encodeDialogueChoice,
  decodeProgress,
  decodeWorldCheck,
  decodeShopStock,
  decodeSpawns,
  decodeZones,
  decodeDefinitions,
  decodeEntityDespawn,
  decodeEntityPositions,
  decodeEntitySpawn,
  decodeGroundItems,
  decodeGroundItemsGone,
  decodeMoveCorrection,
  decodePlayerState,
  decodePrivateMessage,
  decodeAsked,
  decodeVitals,
  decodeTradeState,
  decodeFriends,
  decodeFlying,
  decodeTimeOfDay,
  decodeWorldPlan,
  encodeAllowRequests,
  encodeTradeRequest,
  encodeFriendsRequest,
  decodeRegion,
  decodeShopState,
  decodeSkillUpdate,
  decodeWorldPatch,
  decodeWelcome,
  encodeAdminCommand,
  encodeEditorAction,
  encodeBankMove,
  ChatScope,
  type ChatScopeValue,
  encodeSay,
  encodeCloseInterface,
  encodeHandshake,
  encodeInputFrames,
  encodeCraft,
  encodeDropItem,
  encodeTrackQuest,
  encodeUseOn,
  encodeInteract,
  encodeWatching,
  encodeItemAction,
  encodeSetRetaliate,
  encodeSetStance,
  encodeMoveItem,
  encodeMoveTo,
  encodePing,
  encodePrivateMessageRequest,
  encodeSetRunning,
  encodeShopTrade,
  encodeToolbelt,
  encodeUnequip,
  encodeSetLook,
  decodeOpenMenu,
  PROTOCOL_VERSION,
  REJECT_MESSAGES,
  ServerOp,
  type AdminCommandPacket,
  type AdminFactsPacket,
  type HoldCameraPacket,
  type OpenMenuPacket,
  type TriggersPacket,
  type AdminStatePacket,
  type AppearancePacket,
  type BonusesPacket,
  type ChatPacket,
  type HitPacket,
  type ContainerUpdatePacket,
  type DefinitionsPacket,
  type AnimationPacket,
  type CraftingStatePacket,
  type DropTablePacket,
  type GatheringPacket,
  type DialoguePacket,
  type ProgressPacket,
  type WorldCheckPacket,
  type EditorActionPacket,
  type ShopStockPacket,
  type SpawnsPacket,
  type ZonesPacket,
  type EntityPosition,
  type EntitySpawnPacket,
  type GroundItemWire,
  type InputFrameWire,
  type InteractPacket,
  type WatchingPacket,
  type MoveCorrectionPacket,
  type PlayerStatePacket,
  type PrivateMessagePacket,
  type FlyingPacket,
  type AskedPacket,
  type VitalsPacket,
  type TradeStatePacket,
  type FriendsPacket,
  type TimeOfDayPacket,
  type WorldPlanPacket,
  type FriendActValue,
  type TradeActValue,
  type RegionPacket,
  type ShopStatePacket,
  type SkillUpdatePacket,
  type WorldPatchPacket,
  type WelcomePacket,
  type GroundPacket,
  type GroundGonePacket,
  decodeGround,
  decodeGroundGone,
  decodeSpellbook,
  decodeDevotion,
  decodePlaySound,
  decodeSoundLibrary,
  decodeCollection,
  decodePartyState,
  encodeParty,
  type PartyActValue,
  decodeGuildState,
  encodeGuild,
  type GuildActValue,
  decodeExchangeState,
  encodeExchange,
  type ExchangePacket,
  encodeCastSpell,
  encodeSetAutocast,
  encodeTogglePrayer,
  encodeReport,
  type CastSpellPacket,
  type DevotionPacket,
  type PlaySoundPacket,
} from '@inverse/protocol';

export interface ConnectionEvents {
  welcome(packet: WelcomePacket): void;
  region(packet: RegionPacket): void;
  /** One square of ground, with everything standing on it. */
  ground(packet: GroundPacket): void;
  /** Squares far enough away to forget. */
  groundGone(packet: GroundGonePacket): void;
  /** A moment of black, so something can be moved out of sight. */
  blink(): void;
  playerState(packet: PlayerStatePacket): void;
  entitySpawn(packet: EntitySpawnPacket): void;
  entityDespawn(ids: number[]): void;
  positions(entities: EntityPosition[]): void;
  correction(packet: MoveCorrectionPacket): void;
  chat(packet: ChatPacket): void;
  privateMessage(packet: PrivateMessagePacket): void;
  friends(packet: FriendsPacket): void;
  /** Somebody is asking for something: to be a friend, or to trade. */
  asked(packet: AskedPacket): void;
  /** How a trade stands, or that it has ended and why. */
  tradeState(packet: TradeStatePacket): void;
  /** Every spell and prayer, as the content the tools write. */
  spellbook(json: string): void;
  soundLibrary(json: string): void;
  collection(json: string): void;
  party(json: string): void;
  guild(json: string): void;
  exchange(json: string): void;
  playSound(packet: PlaySoundPacket): void;
  /** Faith, prayers held up, the spell set to cast, and what is lasting on them. */
  devotion(packet: DevotionPacket): void;
  /** Where every region sits, for the map of everywhere. */
  worldPlan(packet: WorldPlanPacket): void;
  definitions(packet: DefinitionsPacket): void;
  container(packet: ContainerUpdatePacket): void;
  appearance(packet: AppearancePacket): void;
  /** A blow landed on somebody, and what it left them on. */
  hit(packet: HitPacket): void;
  /** Something crossing the ground between two fighters. */
  flying(packet: FlyingPacket): void;
  /** What time it is in the world, and whether somebody is holding it there. */
  timeOfDay(packet: TimeOfDayPacket): void;
  /** Somebody died. */
  died(entityId: number): void;
  bonuses(packet: BonusesPacket): void;
  skill(packet: SkillUpdatePacket): void;
  bank(open: boolean): void;
  shop(packet: ShopStatePacket): void;
  admin(packet: AdminStatePacket): void;
  adminFacts(packet: AdminFactsPacket): void;
  /** Every place in the region that does something to whoever walks in. */
  triggers(packet: TriggersPacket): void;
  /** Hold the camera somewhere for a moment. */
  holdCamera(packet: HoldCameraPacket): void;
  /** A window a character opened, which is up until it is closed. */
  openMenu(packet: OpenMenuPacket): void;
  dropTable(packet: DropTablePacket): void;
  /** A crafting station opened or shut, which the server decides. */
  crafting(packet: CraftingStatePacket): void;
  /** Somebody doing something worth watching them do. */
  animation(packet: AnimationPacket): void;
  gathering(packet: GatheringPacket): void;
  worldCheck(packet: WorldCheckPacket): void;
  dialogue(packet: DialoguePacket): void;
  progress(packet: ProgressPacket): void;
  zones(packet: ZonesPacket): void;
  spawns(packet: SpawnsPacket): void;
  shopStock(packet: ShopStockPacket): void;
  worldPatch(packet: WorldPatchPacket): void;
  groundItems(items: GroundItemWire[]): void;
  groundItemsGone(ids: number[]): void;
  vitals(packet: VitalsPacket): void;
  systemUpdate(seconds: number): void;
  rejected(reason: number, message: string): void;
  closed(clean: boolean): void;
  latency(ms: number): void;
}

type Listener<K extends keyof ConnectionEvents> = ConnectionEvents[K];

export class Connection {
  private socket: WebSocket | null = null;
  private readonly listeners = new Map<keyof ConnectionEvents, Set<(...args: never[]) => void>>();

  /** Region origin, needed to decode positions. Set by the region packet. */
  private originX = 0;
  private originZ = 0;

  private pingTimer: number | null = null;
  private handshakeSent = false;

  latency = 0;

  on<K extends keyof ConnectionEvents>(event: K, listener: Listener<K>): () => void {
    let bucket = this.listeners.get(event);
    if (!bucket) {
      bucket = new Set();
      this.listeners.set(event, bucket);
    }
    bucket.add(listener);
    return () => bucket.delete(listener);
  }

  private emit<K extends keyof ConnectionEvents>(
    event: K,
    ...args: Parameters<ConnectionEvents[K]>
  ): void {
    const bucket = this.listeners.get(event);
    if (!bucket) return;
    for (const listener of bucket) {
      (listener as (...values: unknown[]) => void)(...args);
    }
  }

  connect(url: string, ticket: string): void {
    this.close();

    const socket = new WebSocket(url);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;
    this.handshakeSent = false;

    socket.addEventListener('open', () => {
      socket.send(encodeHandshake({ version: PROTOCOL_VERSION, token: ticket }));
      this.handshakeSent = true;

      this.pingTimer = window.setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) socket.send(encodePing(performance.now()));
      }, 5000);
    });

    socket.addEventListener('message', (event: MessageEvent<ArrayBuffer>) => {
      try {
        this.handle(new Uint8Array(event.data));
      } catch (error) {
        // A packet this client cannot read means the two sides disagree about the
        // protocol. Carrying on would render a world that is quietly wrong.
        console.error('could not decode packet', error);
        this.emit('rejected', 0, 'The connection went out of sync. Reload the page.');
        this.close();
      }
    });

    socket.addEventListener('close', () => {
      this.stopPing();
      this.emit('closed', this.handshakeSent);
    });

    socket.addEventListener('error', () => {
      // The close event always follows, and it carries better information.
    });
  }

  close(): void {
    this.stopPing();
    if (this.socket) {
      const socket = this.socket;
      this.socket = null;
      if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
        socket.close();
      }
    }
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  get connected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  private handle(data: Uint8Array): void {
    const reader = new ByteReader(data);
    const opcode = reader.readU8();

    switch (opcode) {
      case ServerOp.Welcome:
        this.emit('welcome', decodeWelcome(reader));
        break;

      case ServerOp.Reject: {
        const reason = reader.readU8();
        this.emit(
          'rejected',
          reason,
          REJECT_MESSAGES[reason] ?? 'The server refused the connection.',
        );
        break;
      }

      case ServerOp.Pong: {
        const clientTime = reader.readF64();
        reader.readF64();
        this.latency = Math.round(performance.now() - clientTime);
        this.emit('latency', this.latency);
        break;
      }

      case ServerOp.RegionData: {
        const region = decodeRegion(reader);
        this.originX = region.originX;
        this.originZ = region.originZ;
        this.emit('region', region);
        break;
      }

      case ServerOp.GroundData:
        this.emit('ground', decodeGround(reader));
        break;

      case ServerOp.GroundGone:
        this.emit('groundGone', decodeGroundGone(reader));
        break;

      case ServerOp.Blink:
        this.emit('blink');
        break;

      case ServerOp.PlayerState:
        this.emit('playerState', decodePlayerState(reader, this.originX, this.originZ));
        break;

      case ServerOp.EntitySpawn:
        this.emit('entitySpawn', decodeEntitySpawn(reader, this.originX, this.originZ));
        break;

      case ServerOp.EntityDespawn:
        this.emit('entityDespawn', decodeEntityDespawn(reader));
        break;

      case ServerOp.EntityPositions:
        this.emit('positions', decodeEntityPositions(reader, this.originX, this.originZ));
        break;

      case ServerOp.MoveCorrection:
        this.emit('correction', decodeMoveCorrection(reader, this.originX, this.originZ));
        break;

      case ServerOp.Chat:
        this.emit('chat', decodeChatMessage(reader));
        break;

      case ServerOp.PrivateMessage:
        this.emit('privateMessage', decodePrivateMessage(reader));
        break;

      case ServerOp.Friends:
        this.emit('friends', decodeFriends(reader));
        break;

      case ServerOp.Asked:
        this.emit('asked', decodeAsked(reader));
        break;

      case ServerOp.TradeState:
        this.emit('tradeState', decodeTradeState(reader));
        break;

      case ServerOp.Spellbook:
        this.emit('spellbook', decodeSpellbook(reader));
        break;

      case ServerOp.Devotion:
        this.emit('devotion', decodeDevotion(reader));
        break;

      case ServerOp.ExchangeState:
        this.emit('exchange', decodeExchangeState(reader));
        break;

      case ServerOp.GuildState:
        this.emit('guild', decodeGuildState(reader));
        break;

      case ServerOp.PartyState:
        this.emit('party', decodePartyState(reader));
        break;

      case ServerOp.Collection:
        this.emit('collection', decodeCollection(reader));
        break;

      case ServerOp.SoundLibrary:
        this.emit('soundLibrary', decodeSoundLibrary(reader));
        break;

      case ServerOp.PlaySound:
        this.emit('playSound', decodePlaySound(reader));
        break;

      case ServerOp.WorldPlan:
        this.emit('worldPlan', decodeWorldPlan(reader));
        break;

      case ServerOp.Definitions:
        this.emit('definitions', decodeDefinitions(reader));
        break;

      case ServerOp.ContainerUpdate:
        this.emit('container', decodeContainerUpdate(reader));
        break;

      case ServerOp.AdminState:
        this.emit('admin', decodeAdminState(reader));
        break;

      case ServerOp.CraftingState:
        this.emit('crafting', decodeCraftingState(reader));
        break;

      case ServerOp.EntityAnimation:
        this.emit('animation', decodeAnimation(reader));
        break;

      case ServerOp.AdminFacts:
        // Read on its own rather than with everything else, because what this
        // carries is a lump of written text for the tools and the tools are the
        // one place where what a packet weighs is up to whoever is building. A
        // conversation that outgrew the wire used to close the connection and
        // throw the person writing it back to the login screen; now it says so
        // and the world carries on.
        try {
          this.emit('adminFacts', decodeAdminFacts(reader));
        } catch (error) {
          console.error('could not read what the tools asked for', error);
          this.emit('chat', {
            entityId: 0,
            name: '',
            message: 'That was too long to send to the tools. Nothing was lost.',
            scope: ChatScope.System,
          });
        }
        break;

      case ServerOp.Triggers:
        this.emit('triggers', decodeTriggers(reader));
        break;

      case ServerOp.OpenMenu:
        this.emit('openMenu', decodeOpenMenu(reader));
        break;

      case ServerOp.HoldCamera:
        this.emit('holdCamera', decodeHoldCamera(reader));
        break;

      case ServerOp.Dialogue:
        this.emit('dialogue', decodeDialogue(reader));
        return;

      case ServerOp.Progress:
        this.emit('progress', decodeProgress(reader));
        return;

      case ServerOp.WorldCheck:
        this.emit('worldCheck', decodeWorldCheck(reader));
        return;

      case ServerOp.Gathering:
        this.emit('gathering', decodeGathering(reader));
        return;

      case ServerOp.DropTable:
        this.emit('dropTable', decodeDropTable(reader));
        break;

      case ServerOp.Zones:
        this.emit('zones', decodeZones(reader));
        break;

      case ServerOp.Spawns:
        this.emit('spawns', decodeSpawns(reader));
        break;

      case ServerOp.ShopStock:
        this.emit('shopStock', decodeShopStock(reader));
        break;

      case ServerOp.WorldPatch:
        this.emit('worldPatch', decodeWorldPatch(reader));
        break;

      case ServerOp.BankState:
        this.emit('bank', reader.readBool());
        break;

      case ServerOp.ShopState:
        this.emit('shop', decodeShopState(reader));
        break;

      case ServerOp.SkillUpdate:
        this.emit('skill', decodeSkillUpdate(reader));
        break;

      case ServerOp.Appearance:
        this.emit('appearance', decodeAppearance(reader));
        break;

      case ServerOp.Hit:
        this.emit('hit', decodeHit(reader));
        break;

      case ServerOp.Flying:
        this.emit('flying', decodeFlying(reader));
        break;

      case ServerOp.TimeOfDay:
        this.emit('timeOfDay', decodeTimeOfDay(reader));
        break;

      case ServerOp.Died:
        this.emit('died', decodeDied(reader));
        break;

      case ServerOp.Bonuses:
        this.emit('bonuses', decodeBonuses(reader));
        break;

      case ServerOp.GroundItems:
        this.emit('groundItems', decodeGroundItems(reader));
        break;

      case ServerOp.GroundItemsGone:
        this.emit('groundItemsGone', decodeGroundItemsGone(reader));
        break;

      case ServerOp.VitalsUpdate:
        this.emit('vitals', decodeVitals(reader));
        break;

      case ServerOp.SystemUpdate:
        this.emit('systemUpdate', reader.readU16());
        break;

      case ServerOp.Disconnect:
        this.emit('rejected', reader.readU8(), 'Disconnected.');
        break;

      default:
        // An unknown opcode is a newer server talking to an older page.
        console.warn('unknown opcode', opcode);
    }
  }

  // -------------------------------------------------------------------------
  // Outgoing
  // -------------------------------------------------------------------------

  private send(data: Uint8Array): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(data);
  }

  sendInput(frames: readonly InputFrameWire[]): void {
    if (frames.length > 0) this.send(encodeInputFrames(frames));
  }

  /** The route is what this client is already walking, for the server to check. */
  sendMoveTo(
    tx: number,
    tz: number,
    plane: number,
    route: readonly { tx: number; tz: number }[] = [],
  ): void {
    this.send(encodeMoveTo({ tx, tz, plane, route: [...route] }));
  }

  sendRunning(running: boolean): void {
    this.send(encodeSetRunning(running));
  }

  sendCastSpell(packet: CastSpellPacket): void {
    this.send(encodeCastSpell(packet));
  }

  sendAutocast(spell: number): void {
    this.send(encodeSetAutocast(spell));
  }

  sendPrayer(prayer: number, on: boolean): void {
    this.send(encodeTogglePrayer(prayer, on));
  }

  sendReport(name: string, reason: string, note: string): void {
    this.send(encodeReport({ name, reason, note }));
  }

  sendChat(message: string, scope: ChatScopeValue = ChatScope.Nearby): void {
    this.send(encodeSay({ scope, message }));
  }

  sendInteract(packet: InteractPacket): void {
    this.send(encodeInteract(packet));
  }

  /**
   * Where the editing camera is looking, so the ground is sent round that.
   *
   * Nothing to do with where the character is. Sent when the camera has moved
   * far enough to change which squares are wanted, and once more when it is let
   * go, which puts the world back on the character.
   */
  sendWatching(packet: WatchingPacket): void {
    this.send(encodeWatching(packet));
  }

  /** An editor action on a whole selection, which is a list rather than a command. */
  sendEditorAction(packet: EditorActionPacket): void {
    this.send(encodeEditorAction(packet));
  }

  sendPrivateMessage(to: string, message: string): void {
    this.send(encodePrivateMessageRequest({ to, message }));
  }

  /** Writing somebody down, or taking them off a list. */
  sendFriends(act: FriendActValue, name: string): void {
    this.send(encodeFriendsRequest({ act, name }));
  }

  /**
   * Which kinds of request this player will hear.
   *
   * Sent on login and again whenever the setting changes. The server is what
   * decides whether a request happens at all: one delivered and then hidden has
   * still interrupted somebody, and whoever sent it is owed an answer rather
   * than silence.
   */
  sendAllowRequests(friends: boolean, trades: boolean): void {
    this.send(encodeAllowRequests({ friends, trades }));
  }

  /**
   * Everything somebody does about a trade.
   *
   * One call for all of it, because the server has one packet for all of it:
   * asking, answering, putting something up, taking it back, agreeing, leaving.
   */
  sendExchange(packet: ExchangePacket): void {
    this.send(encodeExchange(packet));
  }

  sendGuild(act: GuildActValue, text = ''): void {
    this.send(encodeGuild({ act, text }));
  }

  sendParty(act: PartyActValue, name = '', at?: { tx: number; tz: number; layer: number }): void {
    this.send(encodeParty({ act, name, tx: at?.tx ?? 0, tz: at?.tz ?? 0, layer: at?.layer ?? 0 }));
  }

  sendTrade(act: TradeActValue, name = '', slot = 0, count = 0): void {
    this.send(encodeTradeRequest({ act, name, slot, count }));
  }

  sendMoveItem(container: number, from: number, to: number): void {
    this.send(encodeMoveItem({ container, from, to }));
  }

  /** How the player wants to fight, which the server decides the effect of. */
  sendStance(stance: number): void {
    this.send(encodeSetStance(stance));
  }

  /** Whether being hit is answered without being told to. */
  sendRetaliate(on: boolean): void {
    this.send(encodeSetRetaliate(on));
  }

  sendItemAction(container: number, slot: number, action: number): void {
    this.send(encodeItemAction({ container, slot, action }));
  }

  /** What somebody chose to look like, in a window a character opened. */
  sendSetLook(skin: number, eyes: number): void {
    this.send(encodeSetLook({ skin, eyes }));
  }

  sendUnequip(slot: number): void {
    this.send(encodeUnequip(slot));
  }

  /** A tool from the bag onto the belt, or a belt slot back into the bag. */
  sendToolbelt(onto: boolean, slot: number): void {
    this.send(encodeToolbelt({ onto, slot }));
  }

  /** Quantity zero means everything in that slot. */
  sendBankMove(
    deposit: boolean,
    slot: number,
    quantity = 0,
    options: { kit?: boolean; every?: boolean } = {},
  ): void {
    this.send(
      encodeBankMove({
        deposit,
        slot,
        quantity,
        kit: options.kit ?? false,
        every: options.every ?? false,
      }),
    );
  }

  sendShopTrade(buying: boolean, slot: number, quantity = 1): void {
    this.send(encodeShopTrade({ buying, slot, quantity }));
  }

  /** An answer in a conversation. Anything out of range ends it. */
  sendDialogueChoice(option: number): void {
    this.send(encodeDialogueChoice(option));
  }

  sendCloseInterface(): void {
    this.send(encodeCloseInterface());
  }

  /** A command from the development tools. The server decides what it may do. */
  sendAdminCommand(
    command: string,
    numbers: Record<string, number> = {},
    text: Record<string, string> = {},
  ): void {
    const packet: AdminCommandPacket = { command, numbers, text };
    this.send(encodeAdminCommand(packet));
  }

  /** Making something at a station. A count of zero is as many as the materials allow. */
  sendCraft(
    objectId: number,
    tile: { tx: number; tz: number; plane: number },
    action: string,
    count: number,
    /**
     * Which of them, by what it makes.
     *
     * One menu entry can start more than one recipe, so a station with two has
     * to be told which. Nought asks for whichever one there is.
     */
    outputId = 0,
  ): void {
    this.send(encodeCraft({ objectId, ...tile, action, count, outputId }));
  }

  /** Using something on something else: another slot, or something in the world. */
  sendUseOn(
    slot: number,
    target: { kind: number; id: number; tx: number; tz: number; plane: number },
    /** Shift held: keep at it until there is nothing left to do it with. */
    keepGoing = false,
  ): void {
    this.send(encodeUseOn({ slot, ...target, keepGoing }));
  }

  /** Which quest to follow, or an empty name to follow none. */
  sendTrackQuest(quest: string): void {
    this.send(encodeTrackQuest({ quest }));
  }

  /** Quantity zero means everything in the slot. */
  sendDropItem(container: number, slot: number, quantity = 0): void {
    this.send(encodeDropItem({ container, slot, quantity }));
  }
}
